import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { getQueryType, getRequestOptions } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    DefaultPagedResponse,
    Entity,
    IUseQueryPaged,
    PagedResponse,
    Person,
    QueryHookParams,
    QueryParams,
    QueryResult,
    QueryType,
    SurgeonContacts,
} from "../models";
import { Operation } from "rfc6902";
import { useAuth, useQueryParams } from "../contexts";

export interface IUsePersonQuery extends IUseQueryPaged<Person> {
    meQuery: QueryResult<Person>;
    surgeonEmailsQuery: QueryResult<SurgeonContacts[]>;
}

export interface UsePersonQueryParams extends Omit<QueryHookParams, "queryType"> {
    queryType?: QueryType | "surgeon-emails" | "me";
    personType?: string;
    surgeonIds?: number[];
}

export const usePersonQuery = (params?: UsePersonQueryParams): IUsePersonQuery => {
    const personId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { isAuthorized } = useAuth();
    const { get, post, patch, del } = useAxios();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.Person);
    const url = Endpoints.Person;

    const queryType = useMemo(() => {
        if (params?.queryType === "surgeon-emails") return "surgeon-emails";
        if (params?.queryType === "me") return "me";
        return getQueryType(personId, params?.queryType);
    }, [params?.queryType, personId]);

    const args: QueryParams = {
        personType: params?.personType ?? "",
    };

    const queryParamsWithArgs: QueryParams = {
        ...queryParams,
        ...args,
    };

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Person>>(getRequestOptions(url, config, queryParamsWithArgs, sortParams, pageParams)),
        queryKey: queryKeys.person.pagedList({ ...queryParamsWithArgs, ...pageParams, ...sortParams }),
        placeholderData: DefaultPagedResponse,
        enabled: isAuthorized && queryType === QueryType.PagedList,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Person>>(getRequestOptions(url, config, args, sortParams, undefined, false, true)).then(
                (response) => response.items
            ),
        queryKey: queryKeys.person.list({ ...args, ...sortParams }),
        placeholderData: [],
        enabled: isAuthorized && queryType === QueryType.List,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<Person>({ url: `${url}/${personId}`, config }),
        queryKey: queryKeys.person.detail(personId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === QueryType.Detail && !!params?.id,
    });

    const meQuery = useGenericQuery({
        queryFn: (config) => get<Person>({ url: `${url}/me`, config }),
        queryKey: queryKeys.person.me(),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === "me",
        disableErrorToast: true,
    });

    const surgeonEmailsQuery = useGenericQuery({
        queryFn: (config) =>
            get<SurgeonContacts[]>({
                url: `${url}/surgeon-emails`,
                config: { ...config, params: { surgeonIds: params?.surgeonIds?.join(",") ?? "" } },
            }),
        queryKey: queryKeys.person.surgeonEmails({ surgeonIds: params?.surgeonIds }),
        placeholderData: [],
        enabled: isAuthorized && queryType === "surgeon-emails" && !!params?.surgeonIds,
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: Partial<Person>) => post<Partial<Person>, Person>({ url, data }),
        queryKey: queryKeys.person.all(),
        keysToInvalidate: [
            queryKeys.person.all(),
            queryKeys.users.details(),
            queryKeys.surgerySchedulerSurgeons.all(),
            queryKeys.tissueReports.all(),
        ],
        successToast: "Person created",
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) => patch<Operation[], Person>({ url: `${url}/${personId}`, data }),
        queryKey: queryKeys.person.detail(personId),
        keysToInvalidate: [
            queryKeys.person.all(),
            queryKeys.users.details(),
            queryKeys.surgerySchedulerSurgeons.all(),
            queryKeys.tissueReports.all(),
        ],
        successToast: "Person updated",
    });

    const deleteMutation = useGenericMutation({
        mutationFn: () => del({ url: `${url}/${personId}` }),
        queryKey: queryKeys.person.detail(personId),
        keysToInvalidate: [
            queryKeys.person.all(),
            queryKeys.users.details(),
            queryKeys.surgerySchedulerSurgeons.all(),
            queryKeys.tissueReports.all(),
        ],
        successToast: "Person deleted",
    });

    const uploadMutation = useGenericMutation({
        mutationFn: (data: FormData) => post<FormData, Person[]>({ url: `${url}/upload`, data }),
        queryKey: queryKeys.person.all(),
        keysToInvalidate: [
            queryKeys.person.all(),
            queryKeys.users.details(),
            queryKeys.surgerySchedulerSurgeons.all(),
            queryKeys.tissueReports.all(),
        ],
        successToast: "People uploaded",
    });

    return useMemo<IUsePersonQuery>(
        () => ({
            pagedListQuery,
            listQuery,
            detailQuery,
            meQuery,
            surgeonEmailsQuery,
            createMutation,
            updateMutation,
            deleteMutation,
            uploadMutation,
        }),
        [
            pagedListQuery,
            listQuery,
            detailQuery,
            meQuery,
            surgeonEmailsQuery,
            createMutation,
            updateMutation,
            deleteMutation,
            uploadMutation,
        ]
    );
};
